import { VEHICLE_TYPES, buildSiteTree, type SiteNode, type Vehicle, type VehicleType } from "@fts/core";
import { VehicleOperatorsPanel } from "./Operators";
import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { LiveWatchButton } from "../components/LiveWatchButton";
import { EmptyState, PageHeader, Panel } from "../components/ui";
import { useI18n, type MessageKey } from "../i18n";
import { descendantIds, sitePath, vehicleStatus } from "../lib/fleet";
import { navigate, type Route } from "../router/hash";
import { canManageFleet } from "../lib/permissions";
import { useSession, type TenantData } from "../state/session";
import { StatusBadge } from "../components/ui";
import { VehicleDevicePanel } from "./Devices";
import { VehicleChecklistTab, useMaintenanceRequired } from "./VehicleChecklist";
import { VehicleFormDialog } from "./VehicleForm";
import { VehicleTable } from "./VehicleTable";
import { VehicleModulePanels } from "./VehicleModules";
import { AnalogCalibrationPanel } from "./Teltonika";
import { deviceIsTeltonika } from "@fts/core";

/** ADR 0034: the analog calibration panel is shown for vehicles with a Teltonika unit. */
const teltonikaVehicle = (data: TenantData, v: { deviceId: string | null }) => {
  const d = v.deviceId ? data.devices.find((x) => x.deviceId === v.deviceId) : undefined;
  return !!d && deviceIsTeltonika(d.deviceId, d.hw);
};

export function filterVehicles(
  data: Pick<TenantData, "sites" | "vehicles">,
  f: { site?: string | null; type?: string | null; q?: string | null },
): Vehicle[] {
  const siteIds = f.site ? descendantIds(data.sites, f.site) : null;
  const q = f.q?.trim().toLocaleLowerCase("tr") ?? "";
  return data.vehicles.filter((v) => {
    if (siteIds && !siteIds.has(v.siteId)) return false;
    if (f.type && v.type !== f.type) return false;
    if (q) {
      const hay = [v.fleetNo, v.make, v.model, v.serialNo, v.deviceId].filter(Boolean).join(" ").toLocaleLowerCase("tr");
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function siteOptions(tree: SiteNode[], depth = 0, out: Array<{ id: string; label: string }> = []) {
  for (const n of tree) {
    out.push({ id: n.id, label: `${"   ".repeat(depth)}${n.name}` });
    siteOptions(n.children, depth + 1, out);
  }
  return out;
}

export function VehiclesList({ data, route }: { data: TenantData; route: Route }) {
  const { t } = useI18n();
  const { role } = useSession();
  const [creating, setCreating] = useState(false);
  const site = route.query.get("site");
  const type = route.query.get("type");
  const q = route.query.get("q");
  const shown = useMemo(() => filterVehicles(data, { site, type, q }), [data, site, type, q]);
  const options = useMemo(() => siteOptions(buildSiteTree(data.sites, [])), [data.sites]);

  const update = (patch: Record<string, string | null>) => {
    const next = { site, type, q, ...patch };
    navigate("/vehicles", {
      site: next.site || undefined,
      type: next.type || undefined,
      q: next.q || undefined,
    });
  };
  const hasFilter = !!(site || type || q);

  return (
    <>
      <PageHeader
        title={t("vehicles.title")}
        subtitle={t("vehicles.count", { shown: shown.length, total: data.vehicles.length })}
        actions={
          canManageFleet(role) &&
          data.sites.length > 0 && (
            <button type="button" className="button button-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={16} />
              {t("vehicles.add")}
            </button>
          )
        }
      />
      {creating && <VehicleFormDialog data={data} defaultSiteId={site ?? undefined} onClose={() => setCreating(false)} />}
      <div className="filters" role="group" aria-label={t("vehicles.filtersLabel")}>
        <div className="field field-inline">
          <label htmlFor="f-site">{t("vehicles.filterSite")}</label>
          <select id="f-site" value={site ?? ""} onChange={(e) => update({ site: e.target.value || null })}>
            <option value="">{t("vehicles.all")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-type">{t("vehicles.filterType")}</label>
          <select id="f-type" value={type ?? ""} onChange={(e) => update({ type: e.target.value || null })}>
            <option value="">{t("vehicles.all")}</option>
            {VEHICLE_TYPES.map((vt: VehicleType) => (
              <option key={vt} value={vt}>
                {t(`vehicleType.${vt}`)}
              </option>
            ))}
          </select>
        </div>
        {q && (
          <span className="filter-chip">
            {t("vehicles.filterSearch")}: <strong>{q}</strong>
          </span>
        )}
        {hasFilter && (
          <button type="button" className="link-button" onClick={() => navigate("/vehicles")}>
            {t("vehicles.clear")}
          </button>
        )}
      </div>
      <Panel flush>
        {shown.length === 0 ? (
          data.vehicles.length === 0 ? (
            <EmptyState
              compact
              icon="truck"
              title={t("vehicles.none")}
              action={
                canManageFleet(role) && data.sites.length > 0 ? (
                  <button type="button" className="button button-secondary" onClick={() => setCreating(true)}>
                    <Icon name="plus" size={16} />
                    {t("vehicles.add")}
                  </button>
                ) : undefined
              }
            >
              {canManageFleet(role) ? (data.sites.length > 0 ? t("vehicles.noneHintManager") : t("vehicles.noneHintNoSite")) : t("vehicles.noneHintViewer")}
            </EmptyState>
          ) : (
            <EmptyState
              compact
              icon="search"
              title={t("vehicles.empty")}
              action={
                <button type="button" className="button button-secondary button-sm" onClick={() => navigate("/vehicles")}>
                  {t("vehicles.clear")}
                </button>
              }
            />
          )
        ) : (
          <VehicleTable vehicles={shown} sites={data.sites} showSite />
        )}
      </Panel>
    </>
  );
}

export const VEHICLE_TABS = ["general", "live", "events", "checklist", "operators", "device", "audit"] as const;
export type VehicleTab = (typeof VEHICLE_TABS)[number];
const TAB_KEY: Record<VehicleTab, MessageKey> = {
  general: "vehicleDetail.tab.general",
  live: "vehicleDetail.tab.live",
  events: "vehicleDetail.tab.events",
  checklist: "vehicleDetail.tab.checklist",
  operators: "vehicleDetail.tab.operators",
  device: "vehicleDetail.tab.device",
  audit: "vehicleDetail.tab.audit",
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="dl-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function VehicleDetail({ data, vehicleId, tab }: { data: TenantData; vehicleId: string; tab: string | undefined }) {
  const { t } = useI18n();
  const { role } = useSession();
  const [editing, setEditing] = useState(false);
  const v = data.vehicles.find((x) => x.id === vehicleId);
  const maintenance = useMaintenanceRequired(v?.id ?? null);
  if (!v) {
    return <EmptyState icon="truck" title={t("vehicle.notFound")} action={<a href="#/vehicles" className="button button-secondary">{t("vehicles.title")}</a>} />;
  }
  const current: VehicleTab = (VEHICLE_TABS as readonly string[]).includes(tab ?? "") ? (tab as VehicleTab) : "general";
  const path = sitePath(data.sites, v.siteId);
  const base = `#/vehicles/${encodeURIComponent(v.id)}`;
  const dash = t("common.empty");

  const onTabKey = (e: KeyboardEvent) => {
    const i = VEHICLE_TABS.indexOf(current);
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (i + 1) % VEHICLE_TABS.length;
    if (e.key === "ArrowLeft") next = (i - 1 + VEHICLE_TABS.length) % VEHICLE_TABS.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = VEHICLE_TABS.length - 1;
    if (next !== null) {
      e.preventDefault();
      const id = VEHICLE_TABS[next]!;
      window.location.hash = `${base}/${id}`.slice(1);
      requestAnimationFrame(() => document.getElementById(`tab-${id}`)?.focus());
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={<span className="kind-tag">{t(`vehicleType.${v.type}`)}</span>}
        title={<span className="mono">{v.fleetNo}</span>}
        subtitle={path.map((s) => s.name).join(" / ")}
        actions={
          <>
            <StatusBadge status={vehicleStatus(v)} />
            {maintenance && <span className="badge tone-crit">{t("vehicleChecklist.maintenance")}</span>}
            <LiveWatchButton vehicleId={v.id} siteId={v.siteId} sites={data.sites} hasDevice={!!v.deviceId} className="button button-secondary button-sm" />
            {canManageFleet(role) && (
              <button type="button" className="button button-secondary button-sm" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
                {t("common.edit")}
              </button>
            )}
          </>
        }
      />
      {editing && <VehicleFormDialog data={data} vehicle={v} onClose={() => setEditing(false)} />}
      <div className="tabs" role="tablist" aria-label={t("vehicleDetail.tabsLabel")} onKeyDown={onTabKey}>
        {VEHICLE_TABS.map((id) => (
          <a
            key={id}
            id={`tab-${id}`}
            role="tab"
            href={`${base}/${id}`}
            aria-selected={current === id}
            aria-controls="vehicle-tabpanel"
            tabIndex={current === id ? 0 : -1}
            className={current === id ? "tab is-active" : "tab"}
          >
            {t(TAB_KEY[id])}
          </a>
        ))}
      </div>
      <div id="vehicle-tabpanel" role="tabpanel" aria-labelledby={`tab-${current}`} className="tabpanel">
        {current === "general" ? (
          <div className="detail-grid">
            <Panel title={t("vehicleDetail.identity")}>
              <dl className="dl">
                <Field label={t("vehicle.fleetNo")}>
                  <span className="mono">{v.fleetNo}</span>
                </Field>
                <Field label={t("vehicle.type")}>{t(`vehicleType.${v.type}`)}</Field>
                <Field label={t("vehicle.power")}>{t(`power.${v.power}`)}</Field>
                <Field label={t("vehicle.make")}>{v.make ?? dash}</Field>
                <Field label={t("vehicle.model")}>{v.model ?? dash}</Field>
                <Field label={t("vehicle.serialNo")}>
                  <span className="mono">{v.serialNo ?? dash}</span>
                </Field>
                <Field label={t("vehicle.battery")}>
                  <span className="num">{v.batteryVoltage != null ? t("vehicle.batteryValue", { value: v.batteryVoltage }) : dash}</span>
                </Field>
              </dl>
            </Panel>
            <Panel title={t("vehicleDetail.assignment")}>
              <dl className="dl">
                <Field label={t("vehicle.site")}>
                  <a href={`#/sites/${encodeURIComponent(v.siteId)}`} className="subtle-link">
                    {path.map((s) => s.name).join(" / ") || dash}
                  </a>
                </Field>
                <Field label={t("vehicle.device")}>
                  {v.deviceId ? <span className="mono">{v.deviceId}</span> : <span className="muted">{t("vehicle.noDevice")}</span>}
                </Field>
                <Field label={t("vehicle.active")}>{v.active ? t("common.yes") : t("common.no")}</Field>
                <Field label={t("vehicle.status")}>
                  <StatusBadge status={vehicleStatus(v)} />
                </Field>
              </dl>
            </Panel>
            <VehicleDevicePanel data={data} vehicle={v} />
            <VehicleModulePanels vehicle={v} />
            {teltonikaVehicle(data, v) && <AnalogCalibrationPanel vehicle={v} />}
          </div>
        ) : current === "checklist" ? (
          <VehicleChecklistTab data={data} vehicle={v} />
        ) : current === "operators" ? (
          <div className="detail-grid">
            <VehicleOperatorsPanel data={data} vehicle={v} />
          </div>
        ) : current === "device" ? (
          <div className="detail-grid">
            <VehicleDevicePanel data={data} vehicle={v} />
          </div>
        ) : (
          <Panel>
            <EmptyState icon="clock" title={t("soon.title")}>
              {t("vehicleDetail.tabSoon", { tab: t(TAB_KEY[current]) })}
            </EmptyState>
          </Panel>
        )}
      </div>
    </>
  );
}
